/**
 * The chips under the room chat's box when the agent has not proposed any.
 *
 * Pure, and in its own module, so CRA's jest can test it without loading the
 * pane (and the router with it). What it offers follows the ROOM: a room with
 * no question needs opening, a room mid-dig needs aiming, and a room that has
 * stopped needs reading. Offering "chase something specific" to an empty board
 * is how a static prompt bar teaches people the feature is decorative.
 */

export interface RoomPromptState {
  /** The room's question — empty on a brand-new room. */
  question?: string | null;
  /** What the room says success looks like, if it has been set. */
  successCriteria?: string | null;
  /** Is a dig running right now. */
  digging?: boolean;
  /** How many findings (not questions) are on the board. */
  findings?: number;
  /** Axes the room compares on. */
  axes?: string[];
}

// The person is the CEO opening a meeting, so these are things a CEO would say
// to their board before setting the agenda.
const OPENING = [
  'OK, let’s get started',
  'Help me work out what this room should be asking',
  'What can this board find out for me?',
  'Who is sitting at the table?',
];

const UNFRAMED = [
  'What should this market be compared on?',
  'Who should sit in this room?',
  'Start digging',
];

const DIGGING = [
  'What has the room found so far?',
  'Chase ',
  'Is this dig still learning anything?',
];

const IDLE_WITH_FINDINGS = [
  'What has the room found so far?',
  'What is still missing?',
  'Chase ',
  // A ROOM WITH FINDINGS CAN BE ASKED FOR A TABLE. The ready-made ones are
  // shapes somebody anticipated; this is the chip that teaches people the chat
  // will build the cut they actually want. Unfinished on purpose.
  'Build me a table of ',
  'Dig deeper on what we have',
];

/** A prompt ending in a space is an unfinished sentence: the bar puts it in the
 *  box rather than sending it. "Chase " is deliberately one of those. */
export function starterPrompts(state: RoomPromptState): string[] {
  const question = (state.question || '').trim();
  if (!question) return OPENING;
  if (!(state.axes || []).length && !(state.findings || 0)) return UNFRAMED;
  if (state.digging) return DIGGING;
  if (state.findings) return IDLE_WITH_FINDINGS;
  return UNFRAMED;
}
